import { useState } from "react";
import { useGetList, useRecordContext, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { Contact, LeadSubmission } from "../types";
import { toSafeHttpUrl } from "./ContactLeadInfo";

const PAGE_SIZE = 10;

type Answer = { label: string; value: string };

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Flattens a payload (including nested objects such as `details`) into label/value pairs of plain text. */
export const flattenPayload = (payload: unknown, prefix = ""): Answer[] => {
  if (!isPlainObject(payload)) return [];
  return Object.entries(payload).flatMap(([key, value]) => {
    const label = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value)) return flattenPayload(value, label);
    if (value == null || value === "") return [];
    return [
      {
        label,
        value:
          typeof value === "object" ? JSON.stringify(value) : String(value),
      },
    ];
  });
};

/** Newest-first list of a contact's website form submissions, capped and extendable. */
export const ContactLeadSubmissions = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const { data, total } = useGetList<LeadSubmission>(
    "lead_submissions",
    {
      filter: { contact_id: record?.id },
      sort: { field: "submitted_at", order: "DESC" },
      pagination: { page: 1, perPage },
    },
    { enabled: !!record },
  );

  if (!record || !data || data.length === 0) return null;

  return (
    <Card className="mt-4">
      <CardContent>
        <h3 className="text-lg font-semibold mb-2">
          {translate("resources.contacts.lead_info.submissions")}
        </h3>
        <ul className="flex flex-col gap-4">
          {data.map((submission) => {
            const answers = flattenPayload(submission.payload);
            const pageHref = submission.page_url
              ? toSafeHttpUrl(submission.page_url)
              : null;
            return (
              <li
                key={submission.id}
                data-testid="lead-submission"
                className="text-sm border-b last:border-b-0 pb-3"
              >
                <div className="font-medium">
                  {submission.form_type} -{" "}
                  {new Date(submission.submitted_at).toLocaleString()}
                </div>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 mt-1">
                  {submission.page_url && (
                    <>
                      <dt className="text-muted-foreground">
                        {translate("resources.contacts.lead_info.page_url")}
                      </dt>
                      <dd className="break-all">
                        {pageHref ? (
                          <a
                            href={pageHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            {submission.page_url}
                          </a>
                        ) : (
                          submission.page_url
                        )}
                      </dd>
                    </>
                  )}
                  {submission.message && (
                    <>
                      <dt className="text-muted-foreground">
                        {translate("resources.contacts.lead_info.message")}
                      </dt>
                      <dd className="whitespace-pre-wrap break-words">
                        {submission.message}
                      </dd>
                    </>
                  )}
                  {answers.map(({ label, value }) => (
                    <div key={label} className="contents">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="whitespace-pre-wrap break-words">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </li>
            );
          })}
        </ul>
        {total != null && total > data.length && (
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => setPerPage((n) => n + PAGE_SIZE)}
          >
            {translate("resources.contacts.lead_info.show_more")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
